import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DiagnosticsRepository } from '../../../../infrastructure/http/repositories/diagnostics.repository';
import { DialogService } from '../../../../core/services/dialog.service';
import { DiagnosticFileVerificationComponent } from '../diagnostic-file-verification/diagnostic-file-verification.component';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import type {
  DataParityDiagnosticResult,
  DiagnosticResult,
  EmailCallbackDiagnosticResult,
  FileDiagnostic,
  FileDiagnosticResult,
  IngestionTrailDiagnosticResult,
} from '../../models/diagnostics.models';

@Component({
  selector: 'app-diagnostic-request-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DiagnosticFileVerificationComponent,
    ConfirmDialogComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './diagnostic-request-tab.component.html',
  styleUrls: ['./diagnostic-request-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticRequestTabComponent {
  private readonly repo = inject(DiagnosticsRepository);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogService = inject(DialogService);

  requestIdInput = '';
  private currentRequestId: number | null = null;
  diagnosticResult: DiagnosticResult | null = null;
  fileDiagnosticResult: FileDiagnosticResult | null = null;
  ingestionTrailResult: IngestionTrailDiagnosticResult | null = null;
  dataParityResult: DataParityDiagnosticResult | null = null;
  emailCallbackResult: EmailCallbackDiagnosticResult | null = null;
  diagnosticError: string | null = null;
  fileDiagnosticError: string | null = null;
  dataParityDiagnosticError: string | null = null;
  ingestionTrailError: string | null = null;
  emailCallbackError: string | null = null;
  requestDiagnosticConfig: { isLoading: boolean; showSection: boolean } = {
    isLoading: false,
    showSection: false,
  };
  fileDiagnosticConfig: { isLoading: boolean; showSection: boolean } = {
    isLoading: false,
    showSection: false,
  };
  ingestionDiagnosticConfig: { isLoading: boolean; showSection: boolean } = {
    isLoading: false,
    showSection: false,
  };
  dataParityDiagnosticConfig: { isLoading: boolean; showSection: boolean } = {
    isLoading: false,
    showSection: false,
  };
  emailCallbackDiagnosticConfig: { isLoading: boolean; showSection: boolean } =
    { isLoading: false, showSection: false };

  onDiagnose(): void {
    const id = Number.parseInt(this.requestIdInput.trim(), 10);
    if (!id || Number.isNaN(id)) return;
    this.currentRequestId = id;

    this.diagnosticResult = null;
    this.fileDiagnosticResult = null;
    this.ingestionTrailResult = null;
    this.dataParityResult = null;
    this.emailCallbackResult = null;

    this.diagnosticError = null;
    this.fileDiagnosticError = null;
    this.dataParityDiagnosticError = null;
    this.ingestionTrailError = null;
    this.emailCallbackError = null;

    this.requestDiagnosticConfig.isLoading = true;
    this.requestDiagnosticConfig.showSection = true;
    this.fileDiagnosticConfig.isLoading = true;
    this.fileDiagnosticConfig.showSection = true;
    this.ingestionDiagnosticConfig.isLoading = true;
    this.ingestionDiagnosticConfig.showSection = true;
    this.dataParityDiagnosticConfig.isLoading = true;
    this.dataParityDiagnosticConfig.showSection = true;
    this.emailCallbackDiagnosticConfig.isLoading = true;
    this.emailCallbackDiagnosticConfig.showSection = true;

    this.cdr.markForCheck();

    this.refreshRequestSummary();
    this.refreshFileVerification();
    this.refreshDataParity();
    this.refreshIngestionTrail();
    this.refreshEmailCallback();
  }

  onFixVisibility(): void {
    if (!this.diagnosticResult) return;
    const requestId = this.diagnosticResult.request.requestId;
    this.dialogService
      .confirm(`Fix visibility for request ${requestId}?`)
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.repo.fixVisibility(requestId).subscribe(() => this.onDiagnose());
      });
  }

  onReleaseProcessingLock(): void {
    if (!this.diagnosticResult) return;
    const requestId = this.diagnosticResult.request.requestId;
    this.dialogService
      .confirm(`Release processing lock for request ${requestId}?`)
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.repo
          .releaseProcessingLock(requestId)
          .subscribe(() => this.onDiagnose());
      });
  }

  onRetryNetPayCallback(): void {
    if (!this.diagnosticResult) return;
    const requestId = this.diagnosticResult.request.requestId;
    this.dialogService
      .confirm(`Retry NetPay callback for request ${requestId}?`, 'Retry?')
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.repo
          .retryNetPayCallback(requestId)
          .subscribe(() => this.onDiagnose());
      });
  }

  refreshRequestSummary(): void {
    if (!this.currentRequestId) return;
    this.requestDiagnosticConfig.isLoading = true;
    this.diagnosticError = null;
    this.cdr.markForCheck();
    this.repo.getRequestDiagnostic(this.currentRequestId).subscribe({
      next: result => {
        this.requestDiagnosticConfig.isLoading = false;
        this.diagnosticResult = result ?? null;
        if (!result)
          this.diagnosticError = 'Request not found or diagnostic failed.';
        this.cdr.markForCheck();
      },
      error: () => {
        this.requestDiagnosticConfig.isLoading = false;
        this.diagnosticError = 'Request not found or diagnostic failed.';
        this.cdr.markForCheck();
      },
    });
  }

  refreshFileVerification(): void {
    if (!this.currentRequestId) return;
    this.fileDiagnosticConfig.isLoading = true;
    this.fileDiagnosticError = null;
    this.cdr.markForCheck();
    this.repo.getFilesDiagnostic(this.currentRequestId).subscribe({
      next: result => {
        this.fileDiagnosticConfig.isLoading = false;
        this.fileDiagnosticResult = result ?? null;
        if (!result)
          this.fileDiagnosticError = 'File diagnostics fetch failed.';
        this.cdr.markForCheck();
      },
      error: () => {
        this.fileDiagnosticConfig.isLoading = false;
        this.fileDiagnosticError = 'File diagnostics fetch failed.';
        this.cdr.markForCheck();
      },
    });
  }

  refreshIngestionTrail(): void {
    if (!this.currentRequestId) return;
    this.ingestionDiagnosticConfig.isLoading = true;
    this.ingestionTrailError = null;
    this.cdr.markForCheck();
    this.repo.getIngestionTrail(this.currentRequestId).subscribe({
      next: result => {
        this.ingestionDiagnosticConfig.isLoading = false;
        this.ingestionTrailResult = result ?? null;
        if (!result) this.ingestionTrailError = 'Ingestion trail fetch failed.';
        this.cdr.markForCheck();
      },
      error: () => {
        this.ingestionDiagnosticConfig.isLoading = false;
        this.ingestionTrailError = 'Ingestion trail fetch failed.';
        this.cdr.markForCheck();
      },
    });
  }

  refreshDataParity(): void {
    if (!this.currentRequestId) return;
    this.dataParityDiagnosticConfig.isLoading = true;
    this.dataParityDiagnosticError = null;
    this.cdr.markForCheck();
    this.repo.getDataParityDiagnosticResult(this.currentRequestId).subscribe({
      next: result => {
        this.dataParityDiagnosticConfig.isLoading = false;
        this.dataParityResult = result ?? null;
        if (!result)
          this.dataParityDiagnosticError = 'Data parity fetch failed.';
        this.cdr.markForCheck();
      },
      error: () => {
        this.dataParityDiagnosticConfig.isLoading = false;
        this.dataParityDiagnosticError = 'Data parity fetch failed.';
        this.cdr.markForCheck();
      },
    });
  }

  refreshEmailCallback(): void {
    if (!this.currentRequestId) return;
    this.emailCallbackDiagnosticConfig.isLoading = true;
    this.emailCallbackError = null;
    this.cdr.markForCheck();
    this.repo
      .getEmailCallbackDiagnosticResult(this.currentRequestId)
      .subscribe({
        next: result => {
          this.emailCallbackDiagnosticConfig.isLoading = false;
          this.emailCallbackResult = result ?? null;
          if (!result) this.emailCallbackError = 'Email Callback fetch failed.';
          this.cdr.markForCheck();
        },
        error: () => {
          this.emailCallbackDiagnosticConfig.isLoading = false;
          this.emailCallbackError = 'Email Callback fetch failed.';
          this.cdr.markForCheck();
        },
      });
  }

  getMismatchCount(): number {
    return (
      this.fileDiagnosticResult?.files?.filter(
        (f: FileDiagnostic) => f.result === 1
      ).length ?? 0
    );
  }
}
